import React, { useEffect, useState, useCallback } from 'react';
import { 
  Loader, Shield, CheckCircle, Zap, CreditCard, Copy, ArrowLeft, 
  AlertTriangle, Mail, Clock, ChevronDown, ChevronUp 
} from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, PaymentRequestButtonElement, useStripe, useElements } from '@stripe/react-stripe-js';
import CalendarPicker from './CalendarPicker';

function generateIdempotencyKey() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (crypto.getRandomValues(new Uint8Array(1))[0] % 16);
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// 🔥 Stripe publishable key desde variable de entorno. Sin fallback hardcoded:
// si la env var no está configurada en Vercel, mejor que Stripe no se inicialice
// (con un aviso visible) que tener una key live escrita en el repo público.
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  console.error('❌ REACT_APP_STRIPE_PUBLISHABLE_KEY no configurada — Stripe no funcionará.');
}

const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#e2e8f0',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      '::placeholder': { color: '#94a3b8' },
      iconColor: '#94a3b8'
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444'
    }
  },
  hidePostalCode: true
};

// ==================== COMPONENTE STRIPE ====================
function StripeCheckoutForm({ 
  price, 
  customerEmail, 
  isProcessing, 
  setIsProcessing, 
  setError,
  productName
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [cardReady, setCardReady] = useState(false);
  const [cardError, setCardError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!customerEmail?.trim()) {
      setError('El email es obligatorio');
      return;
    }
    
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail)) {
      setError('Introduce un email válido');
      return;
    }

    if (!stripe || !elements) {
      setError('Stripe no está listo. Recarga la página.');
      return;
    }

    if (!cardReady) {
      setError('Por favor, completa los datos de la tarjeta.');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      if (!cardElement) {
        throw new Error('No se pudo acceder al elemento de tarjeta');
      }

      console.log('🔄 Creando PaymentMethod...');

      const { error: stripeError, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: { email: customerEmail }
      });

      if (stripeError) {
        console.error('❌ Error Stripe:', stripeError);
        setError(stripeError.message);
        setIsProcessing(false);
        return;
      }

      console.log('✅ PaymentMethod creado:', paymentMethod.id);
      console.log('🔄 Enviando al backend...');

      // Si el formulario pasó por la pasarela extras (Crea LLC), los recogemos para que
      // el backend pueda registrarlos como filas separadas en Supabase services_contracted.
      let extrasForBackend = null;
      let basePriceForBackend = null;
      try {
        const raw = sessionStorage.getItem('pendingExtras');
        if (raw) {
          const parsed = JSON.parse(raw);
          extrasForBackend = parsed.extras_for_portal || null;
          basePriceForBackend = parsed.base_price ?? null;
        }
      } catch (e) {}

      const res = await fetch(`${BACKEND_URL}/api/charge-card`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethodId: paymentMethod.id,
          amount: Math.round(price * 100),
          email: customerEmail,
          product_name: productName,
          idempotency_key: generateIdempotencyKey(),
          ...(extrasForBackend && extrasForBackend.length ? { extras_for_portal: extrasForBackend } : {}),
          ...(basePriceForBackend != null ? { base_price: basePriceForBackend } : {})
        })
      });

      const responseText = await res.text();
      console.log('📦 Respuesta del servidor:', responseText);

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (parseError) {
        console.error('❌ Error parseando respuesta:', parseError);
        throw new Error(`Error del servidor: ${responseText.substring(0, 100)}`);
      }

      if (data.success) {
        console.log('✅ Pago exitoso');
        localStorage.removeItem('currentPayment');
        window.location.href = '/success';
      } else if (data.requires_action) {
        console.log('🔐 3D Secure requerido');
        const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);
        
        if (confirmError) {
          setError(confirmError.message);
          setIsProcessing(false);
        } else {
          console.log('✅ 3D Secure completado');
          localStorage.removeItem('currentPayment');
          window.location.href = '/success';
        }
      } else {
        setError(data.error || 'Error procesando el pago');
        setIsProcessing(false);
      }

    } catch (err) {
      console.error('❌ Error:', err);
      setError(err.message || 'Error inesperado. Intenta de nuevo.');
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={{ width: '100%' }}>
      <div style={{ 
        padding: '16px',
        background: 'rgba(255, 255, 255, 0.05)', 
        borderRadius: '12px', 
        border: cardError ? '1px solid #ef4444' : '1px solid rgba(255, 255, 255, 0.2)', 
        marginBottom: '20px'
      }}>
        <CardElement 
          options={CARD_ELEMENT_OPTIONS}
          onReady={() => {
            console.log('✅ CardElement listo');
            setCardReady(true);
          }}
          onChange={(e) => {
            if (e.error) {
              setCardError(e.error.message);
              setError(e.error.message);
            } else {
              setCardError(null);
              if (e.complete) {
                setError(null);
              }
            }
          }}
        />
      </div>

      <button 
        type="submit" 
        disabled={isProcessing || !stripe || !elements || !cardReady} 
        className="payment-button"
        style={{
          width: '100%',
          opacity: (isProcessing || !stripe || !elements || !cardReady) ? 0.6 : 1,
          cursor: (isProcessing || !stripe || !elements || !cardReady) ? 'not-allowed' : 'pointer'
        }}
      >
        {isProcessing ? (
          <>
            <Loader className="spinner" style={{ marginRight: '8px', width: '16px', height: '16px' }} />
            Procesando...
          </>
        ) : (
          `Pagar $${price} USD`
        )}
      </button>
    </form>
  );
}

// ==================== APPLE PAY / GOOGLE PAY ====================
// Un único componente que detecta el wallet disponible, renderiza el botón nativo
// de Stripe (Apple Pay negro / Google Pay blanco), y procesa el pago al confirmar
// la sheet. Todo bajo el MISMO árbol <Elements> (crítico: si están separados, el
// paymentRequest creado en una instancia no funciona en la otra).

function WalletWidgetInner({ price, productName, customerEmail, setIsProcessing, setError, onAvailabilityChange }) {
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);

  // 🔥 Crear el paymentRequest y detectar capacidad
  useEffect(() => {
    if (!stripe || !price) return;
    let cancelled = false;

    const pr = stripe.paymentRequest({
      country: 'ES',
      currency: 'usd',
      total: {
        label: productName || 'Pago',
        amount: Math.round(price * 100),
      },
      requestPayerEmail: true,
    });

    pr.canMakePayment().then(result => {
      if (cancelled) return;
      if (!result) {
        onAvailabilityChange?.(null);
        setPaymentRequest(null);
        return;
      }
      // result.applePay → Safari/iOS con Apple Pay configurado.
      // result.googlePay → Chrome/Android con Google Pay disponible.
      let walletKind = null;
      if (result.applePay) walletKind = 'apple';
      else if (result.googlePay) walletKind = 'google';
      else walletKind = 'browser'; // fallback genérico (no mostramos botón en este caso)
      
      if (walletKind === 'apple' || walletKind === 'google') {
        setPaymentRequest(pr);
        onAvailabilityChange?.({ walletKind });
      } else {
        onAvailabilityChange?.(null);
      }
    }).catch(err => {
      console.warn('canMakePayment falló:', err);
      onAvailabilityChange?.(null);
    });

    return () => { cancelled = true; };
  }, [stripe, price, productName, onAvailabilityChange]);

  // 🔥 Listener del paymentmethod (cuando el usuario confirma en la sheet)
  useEffect(() => {
    if (!paymentRequest) return;

    const handler = async (ev) => {
      setIsProcessing(true);
      setError(null);

      // Igual que en tarjeta: recoger extras del formulario si hubo (Crea LLC)
      let extrasForBackendW = null;
      let basePriceForBackendW = null;
      try {
        const raw = sessionStorage.getItem('pendingExtras');
        if (raw) {
          const parsed = JSON.parse(raw);
          extrasForBackendW = parsed.extras_for_portal || null;
          basePriceForBackendW = parsed.base_price ?? null;
        }
      } catch (e) {}

      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api/charge-card`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            paymentMethodId: ev.paymentMethod.id,
            amount: Math.round(price * 100),
            // El email viene del wallet (Apple/Google Pay). Si no lo proveen, usamos el del input.
            email: ev.payerEmail || customerEmail || null,
            product_name: productName,
            wallet_type: ev.walletName, // 'applePay', 'googlePay'
            idempotency_key: generateIdempotencyKey(),
            ...(extrasForBackendW && extrasForBackendW.length ? { extras_for_portal: extrasForBackendW } : {}),
            ...(basePriceForBackendW != null ? { base_price: basePriceForBackendW } : {})
          })
        });

        const data = await res.json();

        if (data.success) {
          ev.complete('success');
          localStorage.removeItem('currentPayment');
          window.location.href = '/success';
        } else if (data.requires_action) {
          const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);
          if (confirmError) {
            ev.complete('fail');
            setError(confirmError.message);
            setIsProcessing(false);
          } else {
            ev.complete('success');
            localStorage.removeItem('currentPayment');
            window.location.href = '/success';
          }
        } else {
          ev.complete('fail');
          setError(data.error || 'Error procesando el pago');
          setIsProcessing(false);
        }
      } catch (err) {
        ev.complete('fail');
        setError(err.message || 'Error procesando el pago');
        setIsProcessing(false);
      }
    };

    paymentRequest.on('paymentmethod', handler);
    return () => {
      paymentRequest.off('paymentmethod', handler);
    };
  }, [paymentRequest, stripe, price, productName, customerEmail, setIsProcessing, setError]);

  if (!paymentRequest) return null;

  // 🔥 Botón nativo de Stripe (Apple Pay negro / Google Pay según el dispositivo).
  // Stripe muestra automáticamente el correcto. El click abre la sheet del SO directamente.
  // Lo envolvemos en un div con la clase crypto-button para que ocupe la misma celda del grid
  // que los otros botones (Tarjeta, USDT, etc.) y se vea visualmente coherente.
  // Altura: el .crypto-button tiene padding 12px 14px (de App.css). El botón nativo Stripe
  // usa 30px → total 30 + 24 = 54px, similar al min-height: 56px del .crypto-button.
  return (
    <div 
      className="crypto-button"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: '8px 14px'
      }}
    >
      <div style={{ width: '100%' }}>
        <PaymentRequestButtonElement 
          options={{ 
            paymentRequest,
            style: {
              paymentRequestButton: {
                type: 'default',
                theme: 'dark',
                height: '36px'
              }
            }
          }}
        />
      </div>
    </div>
  );
}

// Wrapper que monta WalletWidgetInner dentro del provider Elements (UN solo árbol).
function WalletWidget(props) {
  return (
    <Elements stripe={stripePromise}>
      <WalletWidgetInner {...props} />
    </Elements>
  );
}

// ==================== COMPONENTE INPUT ANIMADO PARA DIRECCIÓN ====================
function AddressInputAnimated({ address }) {
  const inputRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);
  const pauseTimeoutRef = React.useRef(null);
  const directionRef = React.useRef(1);
  const pausedAtEndRef = React.useRef(false);
  const isPausedRef = React.useRef(false);
  const scrollPosRef = React.useRef(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Detectar si es móvil
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animación de scroll con setInterval (más predecible)
  useEffect(() => {
    if (!isMobile) return;

    const input = inputRef.current;
    if (!input) return;

    // Velocidad: 1.5 segundos por caracter (~8px), entonces avanzamos 8px cada 1500ms
    // Intervalo de 50ms = avanzamos 8/30 = 0.27px por tick
    const pxPerTick = 0.27;
    const intervalMs = 50;

    const animate = () => {
      if (!inputRef.current || isPausedRef.current || pausedAtEndRef.current) return;
      
      const input = inputRef.current;
      const maxScroll = input.scrollWidth - input.clientWidth;
      
      if (maxScroll <= 0) return;

      scrollPosRef.current += directionRef.current * pxPerTick;

      if (scrollPosRef.current >= maxScroll) {
        scrollPosRef.current = maxScroll;
        input.scrollLeft = maxScroll;
        directionRef.current = -1;
        pausedAtEndRef.current = true;
        setTimeout(() => { pausedAtEndRef.current = false; }, 3000);
      } else if (scrollPosRef.current <= 0) {
        scrollPosRef.current = 0;
        input.scrollLeft = 0;
        directionRef.current = 1;
        pausedAtEndRef.current = true;
        setTimeout(() => { pausedAtEndRef.current = false; }, 3000);
      } else {
        input.scrollLeft = scrollPosRef.current;
      }
    };

    // Iniciar después de un delay
    const startTimeout = setTimeout(() => {
      const interval = setInterval(animate, intervalMs);
      animationFrameRef.current = interval;
    }, 1000);

    return () => {
      clearTimeout(startTimeout);
      if (animationFrameRef.current) {
        clearInterval(animationFrameRef.current);
      }
    };
  }, [isMobile, address]);

  // Pausar al interactuar
  const handleInteraction = () => {
    isPausedRef.current = true;
    
    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current);
    }
    
    // Reanudar después de 1 minuto
    pauseTimeoutRef.current = setTimeout(() => {
      isPausedRef.current = false;
    }, 60 * 1000);
  };

  // Limpiar al desmontar
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) clearTimeout(pauseTimeoutRef.current);
      if (animationFrameRef.current) clearInterval(animationFrameRef.current);
    };
  }, []);

  return (
    <input 
      ref={inputRef}
      type="text" 
      value={address} 
      readOnly 
      className="address-input"
      style={{ 
        overflowX: 'scroll',
        whiteSpace: 'nowrap',
        textOverflow: 'clip',
        outline: 'none',
        boxShadow: 'none'
      }}
      onTouchStart={handleInteraction}
      onTouchMove={handleInteraction}
      onMouseDown={handleInteraction}
      onFocus={(e) => { e.target.blur(); handleInteraction(); }}
    />
  );
}

// ==================== COMPONENTE PRINCIPAL ====================
function PaymentPage({
  price, 
  productName,
  step, setStep, isProcessing, setIsProcessing, error, setError,
  paymentInfo, setPaymentInfo, copied, setCopied, selectedCrypto, setSelectedCrypto,
  customerEmail, setCustomerEmail, timeLeft, setTimeLeft, formatTime, copyToClipboard,
  goBack, paymentMethods, extraPaymentMethods, isFiatMode, setIsFiatMode, handleCryptoPayment,
  requiresCalendar = false
}) {

  // 🔥 Estado para mostrar/ocultar redes adicionales
  const [showMoreNetworks, setShowMoreNetworks] = useState(false);
  
  // 🔥 Estado para el modo de QR (null = oculto, 'wallet' = solo dirección, 'amount' = dirección + monto)
  const [qrMode, setQrMode] = useState(null);

  // 🔥 Calendario: slot seleccionado por el cliente (solo si requiresCalendar)
  // calendarSlotISO: string ISO del momento de inicio en UTC
  // calendarSlotLabel: string legible "Lunes 12 de mayo, 13:00"
  const [calendarSlotISO, setCalendarSlotISO] = useState(null);
  const [calendarSlotLabel, setCalendarSlotLabel] = useState(null);

  const handleSlotSelected = useCallback((iso, label) => {
    setCalendarSlotISO(iso);
    setCalendarSlotLabel(label);
    // Guardar para que Success.js lo recoja tras el pago
    if (requiresCalendar) {
      localStorage.setItem('pendingBooking', JSON.stringify({
        slotISO: iso,
        slotLabel: label,
        productName,
        customerEmail
      }));
    }
  }, [requiresCalendar, productName, customerEmail]);

  // Mantener actualizado el customerEmail en el pendingBooking
  useEffect(() => {
    if (!requiresCalendar || !calendarSlotISO) return;
    const existing = localStorage.getItem('pendingBooking');
    if (!existing) return;
    try {
      const data = JSON.parse(existing);
      if (data.customerEmail !== customerEmail) {
        data.customerEmail = customerEmail;
        localStorage.setItem('pendingBooking', JSON.stringify(data));
      }
    } catch (e) {}
  }, [customerEmail, requiresCalendar, calendarSlotISO]);

  // 🔥 Wallet (Apple Pay / Google Pay) detection state
  // walletInfo: { walletKind: 'apple'|'google' } | null
  // Lo gestiona WalletWidget que llama a onAvailabilityChange.
  const [walletInfo, setWalletInfo] = useState(null);

  const handleWalletDetected = React.useCallback((info) => {
    setWalletInfo(info);
  }, []);

  // 🔥 Detectar si es escritorio (>768px) para reorganizar el grid
  const [isDesktop, setIsDesktop] = useState(() => 
    typeof window !== 'undefined' ? window.innerWidth > 768 : true
  );
  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth > 768);
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  // 🔥 Filtrar paymentMethods según wallet detectado y dispositivo:
  // - Wallet: si es Apple → solo 'apple_pay', si Google → solo 'google_pay'
  // - En desktop CON wallet detectado: ocultar USDT TRC20 (va dentro de "Más Redes")
  //   para que el grid quede equilibrado (Tarjeta + Apple/Google + Más Redes).
  //   Si pulsa "Más Redes" se muestra junto con el resto.
  const filteredPaymentMethods = React.useMemo(() => {
    return paymentMethods.filter(m => {
      if (m.walletKind === 'apple') return walletInfo?.walletKind === 'apple';
      if (m.walletKind === 'google') return walletInfo?.walletKind === 'google';
      // Ocultar USDT TRC20 si: desktop + wallet detectado + NO se ha pulsado "Más Redes"
      if (m.id === 'usdttrc20' && isDesktop && walletInfo && !showMoreNetworks) return false;
      return true;
    });
  }, [paymentMethods, walletInfo, isDesktop, showMoreNetworks]);

  // 🔥 Logos locales desde /img/
  const LOGOS = {
    usdt: '/img/usdt-logo.png',
    usdc: '/img/usdc-logo.png',
    solana: '/img/logo-solana.png',
    tron: '/img/logo-tron.png',
    polygon: '/img/logo-polygon.png',
    ethereum: '/img/logo-ethereum.png',
    bsc: '/img/logo-bnb.png'
  };

  // 🔥 Mapeo de moneda + red + logo (lo que se muestra en QR/dirección)
  const networkNames = {
    // USDT
    'USDTSOL':   { coin: 'USDT', network: 'Solana',      logo: LOGOS.usdt, networkLogo: LOGOS.solana },
    'USDTTRC20': { coin: 'USDT', network: 'TRC20',       logo: LOGOS.usdt, networkLogo: LOGOS.tron },
    'USDTPOL':   { coin: 'USDT', network: 'Polygon',     logo: LOGOS.usdt, networkLogo: LOGOS.polygon },
    'USDTMATIC': { coin: 'USDT', network: 'Polygon',     logo: LOGOS.usdt, networkLogo: LOGOS.polygon },
    'USDTERC20': { coin: 'USDT', network: 'Ethereum',    logo: LOGOS.usdt, networkLogo: LOGOS.ethereum },
    'USDTBSC':   { coin: 'USDT', network: 'BSC (BEP20)', logo: LOGOS.usdt, networkLogo: LOGOS.bsc },
    // USDC
    'USDCSOL':   { coin: 'USDC', network: 'Solana',      logo: LOGOS.usdc, networkLogo: LOGOS.solana },
    'USDCPOL':   { coin: 'USDC', network: 'Polygon',     logo: LOGOS.usdc, networkLogo: LOGOS.polygon },
    'USDCMATIC': { coin: 'USDC', network: 'Polygon',     logo: LOGOS.usdc, networkLogo: LOGOS.polygon },
    'USDCERC20': { coin: 'USDC', network: 'Ethereum',    logo: LOGOS.usdc, networkLogo: LOGOS.ethereum },
    'USDCBSC':   { coin: 'USDC', network: 'BSC (BEP20)', logo: LOGOS.usdc, networkLogo: LOGOS.bsc }
  };

  // 🔥 Logos para el selector de redes (logo del token + red superpuesta)
  const logoMap = {
    'usdttrc20': { main: LOGOS.usdt, network: LOGOS.tron },
    'usdtsol':   { main: LOGOS.usdt, network: LOGOS.solana },
    'usdtpol':   { main: LOGOS.usdt, network: LOGOS.polygon },
    'usdterc20': { main: LOGOS.usdt, network: LOGOS.ethereum },
    'usdtbsc':   { main: LOGOS.usdt, network: LOGOS.bsc },
    'usdcsol':   { main: LOGOS.usdc, network: LOGOS.solana },
    'usdcpol':   { main: LOGOS.usdc, network: LOGOS.polygon },
    'usdcerc20': { main: LOGOS.usdc, network: LOGOS.ethereum },
    'usdcbsc':   { main: LOGOS.usdc, network: LOGOS.bsc }
  };

  // 🔥 Componente para logo con red superpuesta
  const CryptoLogo = ({ cryptoId, size = 24 }) => {
    const logos = logoMap[cryptoId];
    if (!logos) return null;
    
    // Determinar el alt según el cryptoId (USDT o USDC)
    const tokenAlt = cryptoId.startsWith('usdc') ? 'USDC' : 'USDT';
    
    return (
      <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
        <img 
          src={logos.main} 
          alt={tokenAlt}
          style={{ 
            width: size, 
            height: size, 
            borderRadius: '50%',
            objectFit: 'cover'
          }} 
        />
        <img 
          src={logos.network} 
          alt="Network" 
          style={{ 
            position: 'absolute',
            bottom: -1,
            right: -1,
            width: size * 0.5,
            height: size * 0.5,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '1.5px solid #ffffff',
            background: '#ffffff'
          }} 
        />
      </div>
    );
  };

  const getCoinInfo = (currency) => {
    if (!currency) return { coin: '', network: '', logo: '' };
    return networkNames[currency.toUpperCase()] || { coin: currency, network: '', logo: '' };
  };

  useEffect(() => {
    const saved = localStorage.getItem('currentPayment');
    if (saved && step === 'payment') {
      try {
        const data = JSON.parse(saved);
        if (data.step === 'showing-address') {
          setStep('showing-address');
          setPaymentInfo(data);
          setSelectedCrypto(data.selectedCrypto || 'usdttrc20');
          setCustomerEmail(data.customerEmail || '');
          setTimeLeft(data.timeLeft || 2 * 60 * 60);
        }
      } catch (e) {
        console.error('Error parseando localStorage:', e);
      }
    }
  }, [step, setStep, setPaymentInfo, setSelectedCrypto, setCustomerEmail, setTimeLeft]);


  // Temporizador 2 horas (alineado con TTL backend)

 useEffect(() => {
  if (step !== 'showing-address') return;

  // Guardar timestamp de inicio si no existe
  const saved = localStorage.getItem('currentPayment');
  let startTime = Date.now();

  if (saved) {
    const data = JSON.parse(saved);
    if (data.startTime) {
      startTime = data.startTime;
    } else {
      data.startTime = startTime;
      localStorage.setItem('currentPayment', JSON.stringify(data));
    }
  }

  // 🔥 TTL alineado con el backend (2h). Antes 20 min, pero las redes lentas
  // (BSC saturada, ERC20 gas alto) pueden tardar más de 20 min en confirmar y
  // el backend ahora mantiene la orden viva 2h con reconciliación periódica.
  const PAY_TTL_SEC = 2 * 60 * 60; // 2 horas

  const timer = setInterval(() => {
    const elapsed = Math.floor((Date.now() - startTime) / 1000);
    const remaining = Math.max(0, PAY_TTL_SEC - elapsed);

    setTimeLeft(remaining);

    if (remaining <= 0) {
      clearInterval(timer);
      localStorage.removeItem('currentPayment');
      window.location.href = '/cancel';
    }
  }, 1000);

  return () => clearInterval(timer);
}, [step, setTimeLeft]);

  // Temporizador Fin


  useEffect(() => {
    if (step !== 'showing-address' || !paymentInfo?.orderId) return;

    let hasRedirected = false;
    
    const checkPaymentStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/check-payment/${paymentInfo.orderId}`);
        const data = await res.json();

        // Cualquier estado que no sea "waiting" → ir a /confirming
        if (data.status !== 'waiting' && data.status !== 'expired' && !hasRedirected) {
          hasRedirected = true;
          console.log('🔄 PAGO DETECTADO - Redirigiendo a /confirming');
          window.location.href = '/confirming';
        }
      } catch (err) {
        console.error('⚠️ Error:', err);
      }
    };

    checkPaymentStatus();
    const interval = setInterval(checkPaymentStatus, 3000);

    return () => clearInterval(interval);
  }, [step, paymentInfo]);

  // ==================== PANTALLA PRINCIPAL ====================
  if (step === 'payment') {
    // 🔥 Función para volver al formulario
    const handleBackToForm = () => {
      const returnUrl = sessionStorage.getItem('formReturnUrl');
      if (returnUrl) {
        window.location.href = returnUrl;
      } else {
        window.history.back();
      }
    };

    return (
      <div className="app-container">
        <div className="payment-card">
          {/* Botón volver al formulario */}
          <button onClick={handleBackToForm} className="back-button">
            <ArrowLeft className="back-icon" /> Volver al formulario
          </button>

          <div className="card-header">
            <div className="icon-shield"><Shield className="icon-large" /></div>
            <h1 className="title">Pago Seguro</h1>
            <p className="subtitle">Selecciona tu método de pago</p>
          </div>

          <div className="product-details">
            <div className="detail-row">
              <span className="label">Servicio</span>
              <span className="value">{productName || 'Servicio'}</span>
            </div>
            <div className="detail-row">
              <span className="label">Precio</span>
              <span className="price">${price} USD</span>
            </div>
            {requiresCalendar && calendarSlotLabel && (
              <div className="detail-row">
                <span className="label">📅 Cita</span>
                <span className="value" style={{ fontSize: '14px' }}>{calendarSlotLabel}</span>
              </div>
            )}
          </div>

          {/* 🔥 Si la asesoría requiere calendario y aún no hay slot, mostrar el picker
              en lugar del selector de pago */}
          {requiresCalendar && !calendarSlotISO ? (
            <div style={{ marginTop: '16px' }}>
              <h3 style={{ 
                fontSize: '16px', 
                color: '#fff', 
                marginBottom: '8px',
                fontFamily: "'Space Grotesk', sans-serif"
              }}>
                📅 Selecciona fecha y hora
              </h3>
              <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)', marginBottom: '12px' }}>
                Elige el día y hora para tu asesoría (45 minutos, hora de Madrid).
              </p>
              <CalendarPicker onSlotSelected={handleSlotSelected} embedded={false} />
            </div>
          ) : (
          <>
          {requiresCalendar && calendarSlotLabel && (
            <div style={{
              background: 'rgba(74,222,128,0.1)',
              border: '1px solid rgba(74,222,128,0.3)',
              borderRadius: '8px',
              padding: '12px 14px',
              margin: '12px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              flexWrap: 'wrap'
            }}>
              <div style={{ fontSize: '14px', color: '#86efac' }}>
                ✅ Cita: <strong>{calendarSlotLabel}</strong>
              </div>
              <button
                onClick={() => { setCalendarSlotISO(null); setCalendarSlotLabel(null); }}
                style={{
                  background: 'rgba(255,255,255,0.08)',
                  border: '1px solid rgba(255,255,255,0.15)',
                  color: '#fff',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  cursor: 'pointer'
                }}
              >
                Cambiar
              </button>
            </div>
          )}

          <div className="crypto-selector">
            <label className="selector-label">Pagar con:</label>
            <div className="crypto-grid">
              {/* Botón Tarjeta */}
              <button 
                onClick={() => { 
                  setIsFiatMode(true); 
                  setSelectedCrypto(null); 
                  setError(null); 
                }} 
                className={`crypto-button ${isFiatMode && !selectedCrypto ? 'selected' : ''}`}
              >
                <div className="crypto-content">
                  <span className="crypto-symbol" style={{ color: (isFiatMode && !selectedCrypto) ? '#fff' : '#a855f7' }}>
                    <CreditCard size={24} />
                  </span>
                  <div className="crypto-info">
                    <span className="crypto-name">Tarjeta</span>
                    <span className="crypto-network">Débito / Crédito</span>
                  </div>
                </div>
              </button>

              {/* 🔥 Apple Pay / Google Pay NATIVO de Stripe.
                  Solo se monta SI el dispositivo soporta el wallet. Si no, no aparece y
                  los siguientes botones (cripto) ocupan ese sitio en el grid. */}
              <WalletWidget 
                price={price}
                productName={productName}
                customerEmail={customerEmail}
                setIsProcessing={setIsProcessing}
                setError={setError}
                onAvailabilityChange={handleWalletDetected}
              />

              {/* 🔥 Redes principales cripto (excluye fiat: card, apple_pay, google_pay) */}
              {filteredPaymentMethods.filter(m => !m.isFiat).map(crypto => (
                <button 
                  key={crypto.id} 
                  onClick={() => { 
                    setSelectedCrypto(crypto.id); 
                    setIsFiatMode(false); 
                    setError(null); 
                  }} 
                  className={`crypto-button ${selectedCrypto === crypto.id ? 'selected' : ''}`}
                >
                  <div className="crypto-content">
                    <CryptoLogo cryptoId={crypto.id} size={24} />
                    <div className="crypto-info">
                      <span className="crypto-name">{crypto.name}</span>
                      <span className="crypto-network">{crypto.network}</span>
                    </div>
                  </div>
                </button>
              ))}

              {/* 🔥 Botón "Más Redes" con logos previsualizados.
                  En móvil los logos van en cinta animada (derecha → izquierda en bucle)
                  para mantener el tamaño del botón sin que crezca. */}
              <style>{`
                /* 🔥 Forzar altura idéntica en todos los botones del grid (móvil + desktop).
                   Sin esto, Apple/Google Pay y "Más Redes" salen más altos por el contenido
                   embebido (iframe Stripe / logos extra). */
                .crypto-grid > .crypto-button,
                .crypto-grid .crypto-button {
                  min-height: 56px;
                  height: 56px;
                  box-sizing: border-box;
                  display: flex;
                  align-items: center;
                  overflow: hidden;
                }
                .crypto-grid .crypto-content {
                  width: 100%;
                  align-items: center;
                }
                /* En móvil bajamos un poco la altura para que sea coherente con padding ajustado */
                @media (max-width: 768px) {
                  .crypto-grid > .crypto-button,
                  .crypto-grid .crypto-button {
                    height: 54px;
                    min-height: 54px;
                  }
                }
                /* 🔥 Animación de cinta de logos de redes (solo móvil) */
                @keyframes logos-conveyor {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                /* Versión desktop: ambos sets visibles quietos */
                .more-networks-coins-wrap,
                .more-networks-logos-wrap {
                  display: inline-flex;
                  align-items: center;
                  gap: 4px;
                }
                .more-networks-coins-conveyor,
                .more-networks-conveyor {
                  display: none;
                }
                @media (max-width: 768px) {
                  .more-networks-coins-wrap,
                  .more-networks-logos-wrap {
                    display: none;
                  }
                  .more-networks-coins-conveyor,
                  .more-networks-conveyor {
                    display: inline-block;
                    position: relative;
                    overflow: hidden;
                    flex: 1;
                    vertical-align: middle;
                    -webkit-mask-image: linear-gradient(to right, transparent 0, #000 10%, #000 90%, transparent 100%);
                            mask-image: linear-gradient(to right, transparent 0, #000 10%, #000 90%, transparent 100%);
                  }
                  .more-networks-coins-conveyor {
                    height: 18px;
                    min-width: 50px;
                    margin-left: 6px;
                  }
                  .more-networks-conveyor {
                    height: 14px;
                    min-width: 60px;
                    margin-left: 4px;
                  }
                  .more-networks-coins-conveyor-track,
                  .more-networks-conveyor-track {
                    display: inline-flex;
                    align-items: center;
                    white-space: nowrap;
                    animation: logos-conveyor 8s linear infinite;
                    will-change: transform;
                  }
                  .more-networks-coins-conveyor-track {
                    gap: 10px;
                    padding-right: 10px;
                  }
                  .more-networks-conveyor-track {
                    gap: 8px;
                    padding-right: 8px;
                  }
                  .more-networks-coins-conveyor-track img {
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    object-fit: cover;
                    flex-shrink: 0;
                  }
                  .more-networks-conveyor-track img {
                    width: 12px;
                    height: 12px;
                    border-radius: 50%;
                    object-fit: cover;
                    flex-shrink: 0;
                  }
                }
              `}</style>
              <button 
                onClick={() => setShowMoreNetworks(!showMoreNetworks)} 
                className="crypto-button"
                style={{ 
                  background: 'rgba(255, 255, 255, 0.03)',
                  borderStyle: 'dashed'
                }}
              >
                <div className="crypto-content">
                  <span className="crypto-symbol" style={{ color: '#FFFFFF', flexShrink: 0 }}>
                    {showMoreNetworks ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </span>
                  <div className="crypto-info" style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                    {/* Línea principal: "Más Redes" + logos USDT y USDC */}
                    <span className="crypto-name" style={{ 
                      color: '#FFFFFF',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}>
                      <span style={{ flexShrink: 0 }}>Más Redes</span>
                      {/* Versión desktop: logos quietos */}
                      <span className="more-networks-coins-wrap">
                        <img src={LOGOS.usdt} alt="USDT" style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} />
                        <img src={LOGOS.usdc} alt="USDC" style={{ width: '16px', height: '16px', borderRadius: '50%', objectFit: 'cover' }} />
                      </span>
                      {/* Versión móvil: cinta animada */}
                      <span className="more-networks-coins-conveyor" aria-hidden="true">
                        <span className="more-networks-coins-conveyor-track">
                          <img src={LOGOS.usdt} alt="" />
                          <img src={LOGOS.usdc} alt="" />
                          {/* duplicado para bucle sin saltos */}
                          <img src={LOGOS.usdt} alt="" />
                          <img src={LOGOS.usdc} alt="" />
                        </span>
                      </span>
                    </span>
                    {/* Línea secundaria: "Mostrar más" + 5 logos de redes */}
                    <span className="crypto-network" style={{ 
                      color: '#9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden'
                    }}>
                      <span style={{ flexShrink: 0 }}>{showMoreNetworks ? 'Ocultar' : 'Mostrar más'}</span>
                      {!showMoreNetworks && (
                        <>
                          {/* Versión desktop: todos los logos visibles */}
                          <span className="more-networks-logos-wrap">
                            <img src={LOGOS.tron}     alt="Tron"     style={{ width: '12px', height: '12px', borderRadius: '50%', objectFit: 'cover', marginLeft: '4px' }} />
                            <img src={LOGOS.solana}   alt="Solana"   style={{ width: '12px', height: '12px', borderRadius: '50%', objectFit: 'cover' }} />
                            <img src={LOGOS.bsc}      alt="BSC"      style={{ width: '12px', height: '12px', borderRadius: '50%', objectFit: 'cover' }} />
                            <img src={LOGOS.polygon}  alt="Polygon"  style={{ width: '12px', height: '12px', borderRadius: '50%', objectFit: 'cover' }} />
                            <img src={LOGOS.ethereum} alt="Ethereum" style={{ width: '12px', height: '12px', borderRadius: '50%', objectFit: 'cover' }} />
                          </span>
                          {/* Versión móvil: cinta animada (derecha → izquierda, bucle infinito) */}
                          <span className="more-networks-conveyor" aria-hidden="true">
                            <span className="more-networks-conveyor-track">
                              <img src={LOGOS.tron}     alt="" />
                              <img src={LOGOS.solana}   alt="" />
                              <img src={LOGOS.bsc}      alt="" />
                              <img src={LOGOS.polygon}  alt="" />
                              <img src={LOGOS.ethereum} alt="" />
                              {/* Duplicado para que no haya hueco vacío entre ciclos */}
                              <img src={LOGOS.tron}     alt="" />
                              <img src={LOGOS.solana}   alt="" />
                              <img src={LOGOS.bsc}      alt="" />
                              <img src={LOGOS.polygon}  alt="" />
                              <img src={LOGOS.ethereum} alt="" />
                            </span>
                          </span>
                        </>
                      )}
                    </span>
                  </div>
                </div>
              </button>

              {/* 🔥 Redes adicionales (se muestran al hacer clic en "Más Redes") */}
              {showMoreNetworks && extraPaymentMethods && extraPaymentMethods.map(crypto => (
                <button 
                  key={crypto.id} 
                  onClick={() => { 
                    setSelectedCrypto(crypto.id); 
                    setIsFiatMode(false); 
                    setError(null); 
                  }} 
                  className={`crypto-button ${selectedCrypto === crypto.id ? 'selected' : ''}`}
                >
                  <div className="crypto-content">
                    <CryptoLogo cryptoId={crypto.id} size={24} />
                    <div className="crypto-info">
                      <span className="crypto-name">{crypto.name}</span>
                      <span className="crypto-network">{crypto.network}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="email-section">
            <label className="email-label">
              <Mail className="email-icon" /> Email para confirmación *
            </label>
            <input 
              type="email" 
              value={customerEmail} 
              onChange={e => setCustomerEmail(e.target.value)} 
              placeholder="tu@email.com" 
              className="email-input" 
            />
            {walletInfo && (walletInfo.walletKind === 'apple' || walletInfo.walletKind === 'google') && (
              <p style={{ 
                fontSize: '12px', 
                color: 'rgba(255,255,255,0.6)', 
                marginTop: '6px',
                marginBottom: 0 
              }}>
                * No necesario si pagas con {walletInfo.walletKind === 'apple' ? 'Apple Pay' : 'Google Pay'} (se obtiene automáticamente)
              </p>
            )}
          </div>

          {error && (
            <div style={{ 
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '8px',
              marginBottom: '16px'
            }}>
              <p style={{ 
                color: '#fca5a5', 
                fontWeight: '400', 
                fontSize: '14px',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertTriangle size={16} />
                {error}
              </p>
            </div>
          )}

          {isFiatMode && (
            <div style={{ marginTop: '20px' }}>
              <Elements stripe={stripePromise}>
                <StripeCheckoutForm 
                  price={price}
                  customerEmail={customerEmail}
                  isProcessing={isProcessing}
                  setIsProcessing={setIsProcessing}
                  setError={setError}
                  productName={productName}
                />
              </Elements>
            </div>
          )}

          {!isFiatMode && (
            <>
              <div className="features">
                <div className="feature-item">
                  <CheckCircle className="feature-icon check" />
                  <span>Pago instantáneo</span>
                </div>
                <div className="feature-item">
                  <Shield className="feature-icon shield" />
                  <span>100% seguro</span>
                </div>
                <div className="feature-item">
                  <Zap className="feature-icon zap" />
                  <span>0% comisiones</span>
                </div>
              </div>

              <button
                onClick={handleCryptoPayment}
                disabled={isProcessing}
                className="payment-button"
              >
                {isProcessing ? (
                  <>
                    <Loader className="spinner" style={{ marginRight: '8px' }} />
                    Procesando...
                  </>
                ) : (
                  'Continuar al Pago'
                )}
              </button>
            </>
          )}

          {isFiatMode && (
            <div className="features" style={{ marginTop: '20px' }}>
              <div className="feature-item">
                <CheckCircle className="feature-icon check" />
                <span>Pago instantáneo</span>
              </div>
              <div className="feature-item">
                <Shield className="feature-icon shield" />
                <span>100% seguro</span>
              </div>
            </div>
          )}
          </>
          )}

          <p className="footer-text">Transacción segura · 0 intermediarios</p>
        </div>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="app-container">
        <div className="payment-card">
          <div className="processing-container">
            <Loader className="spinner-large" />
            <h2 className="processing-title">Generando dirección...</h2>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'showing-address' && paymentInfo) {
    const coinInfo = getCoinInfo(paymentInfo.currency);
    
    // 🔥 Mostrar aviso de "confirmando" cuando ha transcurrido al menos 1 min desde el inicio.
    // Con TTL 2h (7200s), esto es timeLeft <= 7140.
    const showConfirmationNotice = timeLeft <= 7140;

    // 🔥 Generar QR solo con wallet (sin monto)
    const qrWalletOnly = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${paymentInfo.address}`;
    
    return (
      <div className="app-container">
        <div className="payment-card wide">
          <button onClick={goBack} className="back-button">
            <ArrowLeft className="back-icon" /> Volver
          </button>

          {/* Header con tick verde */}
          <div className="card-header">
            <div className="icon-shield success">
              <CheckCircle className="icon-large" />
            </div>
            <h1 className="title">Dirección de Pago Generada</h1>
            <p className="subtitle">Escanea el QR o copia la dirección</p>
          </div>

          {/* Cantidad a enviar con logos */}
          <div className="payment-amount-box">
            <p className="amount-label">Cantidad exacta a enviar</p>
            <p className="amount-value" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              {paymentInfo.amount} {coinInfo.coin}
              <img 
                src={coinInfo.logo || LOGOS.usdt} 
                alt={coinInfo.coin} 
                style={{ width: '28px', height: '28px', borderRadius: '50%' }} 
              />
            </p>
            <p className="amount-currency" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
              Red {coinInfo.network}
              {coinInfo.networkLogo && (
                <img 
                  src={coinInfo.networkLogo} 
                  alt={coinInfo.network} 
                  style={{ width: '18px', height: '18px', borderRadius: '50%' }} 
                />
              )}
            </p>
          </div>

          {/* Dirección de pago con scroll animado en móvil */}
          <div className="address-section">
            <label className="address-label">Dirección de pago:</label>
            <div className="address-input-group">
              <AddressInputAnimated address={paymentInfo.address} />
              <button 
                onClick={() => copyToClipboard(paymentInfo.address)}
                className="copy-button"
                style={{ width: '48px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
              >
                {copied ? (
                  <CheckCircle className="copy-icon" style={{ color: '#10b981' }} />
                ) : (
                  <Copy className="copy-icon" />
                )}
              </button>
            </div>
          </div>

          {/* Timer */}
          <div className={`timer-box ${timeLeft <= 300 ? 'low-time' : ''}`}>
            <Clock className="timer-icon" />
            <span className="timer-value">{formatTime(timeLeft)}</span>
          </div>

          {/* Aviso pulsante de confirmación automática */}
          {showConfirmationNotice && (
            <div style={{ 
              background: 'rgba(74, 222, 128, 0.1)',
              border: '1px solid rgba(74, 222, 128, 0.3)',
              borderRadius: '8px',
              padding: '12px',
              marginTop: '16px',
              marginBottom: '16px',
              animation: 'pulse 2s ease-in-out infinite'
            }}>
              <p style={{ 
                color: '#86efac', 
                fontWeight: '400', 
                fontSize: '14px',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <CheckCircle size={16} />
                Confirmación automática en ≈45 segundos desde el pago, no abandones la página al pagar
              </p>
            </div>
          )}

          <style>
            {`
              @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.7; }
              }
            `}
          </style>

          {/* 🔥 QR Plegable con 3 botones */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button
                onClick={() => setQrMode(qrMode === 'wallet' ? null : 'wallet')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: qrMode === 'wallet' ? 'rgba(188,80,218,0.2)' : 'rgba(255,255,255,0.05)',
                  border: qrMode === 'wallet' ? '2px solid var(--primary-accent)' : '2px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                QR Wallet
              </button>
              <button
                onClick={() => setQrMode(qrMode === 'amount' ? null : 'amount')}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: qrMode === 'amount' ? 'rgba(188,80,218,0.2)' : 'rgba(255,255,255,0.05)',
                  border: qrMode === 'amount' ? '2px solid var(--primary-accent)' : '2px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#fff',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                QR + Monto
              </button>
              <button
                onClick={() => setQrMode(null)}
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  background: qrMode === null ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.05)',
                  border: '2px solid rgba(255,255,255,0.12)',
                  borderRadius: '8px',
                  color: '#9ca3af',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.3s'
                }}
              >
                Ocultar
              </button>
            </div>
            
            {/* QR desplegado */}
            {qrMode && (
              <div className="qr-container" style={{ animation: 'slideIn 0.3s ease' }}>
                <img 
                  src={qrMode === 'wallet' ? qrWalletOnly : paymentInfo.qrCode} 
                  alt="QR Code" 
                  className="qr-image" 
                />
                <p className="qr-text">
                  {qrMode === 'wallet' ? 'Solo dirección' : 'Dirección + Monto'}
                </p>
              </div>
            )}
          </div>

          {/* Warning ATENCIÓN */}
          <div className="warning-box">
            <div className="warning-icon-container">
              <AlertTriangle className="warning-icon" />
            </div>
            <div>
              <p className="warning-title">ATENCIÓN</p>
              <p className="warning-text" style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                Envía exactamente <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {paymentInfo.amount} {coinInfo.coin}
                  <img src={coinInfo.logo || LOGOS.usdt} alt={coinInfo.coin} style={{ width: '14px', height: '14px', borderRadius: '50%' }} />
                </strong> a esta dirección usando la red 
                <strong style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  {coinInfo.network}
                  {coinInfo.networkLogo && (
                    <img src={coinInfo.networkLogo} alt={coinInfo.network} style={{ width: '14px', height: '14px', borderRadius: '50%' }} />
                  )}
                </strong>
              </p>
              <p className="warning-text" style={{ marginTop: '8px', fontSize: '13px', opacity: 0.9 }}>
                Si envías desde un Exchange, revisa las comisiones de retiro para asegurarte de enviar el importe correcto
              </p>
            </div>
          </div>

          {/* Status esperando */}
          <div className="status-box">
            <Loader className="status-spinner" />
            <div>
              <p className="status-title">Esperando confirmación...</p>
              <p className="status-text">Recibirás confirmación automáticamente</p>
              {customerEmail && (
                <p className="status-text">Enviaremos confirmación a: {customerEmail}</p>
              )}
            </div>
          </div>

          <p className="order-id">ID: {paymentInfo.orderId}</p>
        </div>
      </div>
    );
  }

  return null;
}

export default PaymentPage;